import {
  getActiveRegion,
  getActiveTenant,
  getDomainName,
  getInfraDetails,
} from 'api-client/helpers'
import {
  ApplicationCredentialType,
  getApplicationSecretName,
  getInfraRegionURL,
} from '../secrets/helper'
import { sessionStoreKey } from 'core/session/sessionReducers'
import store from 'app/store'

export interface SshKeyObject {
  name: string
  public_key: string
}

/**
 * Returns the cluster endpoint for the given cluster name with current domain and tenant
 * @param param0 cluster name
 * @returns cluster endpoint
 */
export const getClusterEndpoint = ({ clusterName }) => {
  const hostname = new URL(getInfraRegionURL()).host
  const domain = getDomainName()
  const tenant = getActiveTenant()
  return String(`${clusterName}.${domain}-${tenant}.k8s.${hostname}`).toLowerCase()
}

export const isPrivateRegistryEnabled = () => {
  const state = store.getState()
  const { features } = state[sessionStoreKey]
  return features?.kaapi?.enable_private_registry === false ? false : true
}

interface kaapiClusterClassParams {
  name: string
  namespace: string
  isBYOCluster?: boolean
  gpuAddons?: boolean
  network?: string
  subnet?: string
  openstackCluster?: string
  nodePools?: any[]
  volumeType?: string
  volumeSize?: number
  sourceType?: string
  image?: string
  region?: string
  domain?: string
  tenant?: string
  kubernetesVersion?: string
  apiServerFlags?: Array<string>
  schedulerFlags?: Array<string>
  controllerManagerFlags?: Array<string>
  duInternalIP?: string
  isAirgapped?: boolean
  replicas?: number
  sshKey?: SshKeyObject
}

interface MachineDeploymentParams {
  pool: any
  index: number
  region: string
  openstackCluster: string
  image: string
  sourceType: string
  volumeSize?: number
  volumeType?: string
  sshKey?: SshKeyObject
}

const buildSshKeyVariables = (sshKey?: SshKeyObject) =>
  sshKey ? { sshKeyName: sshKey.name, sshAuthorizedKey: sshKey.public_key } : {}

const buildMachineDeployment = ({
  pool,
  index,
  region,
  openstackCluster,
  image,
  sourceType,
  volumeSize,
  volumeType,
  sshKey,
}: MachineDeploymentParams) => ({
  class: 'ost-md',
  name: `md-${index}`,
  metadata: {
    labels: {
      'pcd-kaapi.pf9.io/region': region,
    },
    ...(pool.autoscaling
      ? {
          annotations: {
            'cluster.x-k8s.io/cluster-api-autoscaler-node-group-min-size': String(pool.replicas),
            'cluster.x-k8s.io/cluster-api-autoscaler-node-group-max-size': String(
              pool.autoscaling.max,
            ),
          },
        }
      : {}),
  },
  ...(!pool.autoscaling && { replicas: pool.replicas || 1 }),
  failureDomain: openstackCluster,
  variables: {
    overrides: [
      {
        name: 'workerNode',
        value: {
          flavor: pool.flavor,
          imageName: image,
          ...buildSshKeyVariables(sshKey),
        },
      },
      {
        name: 'network',
        value: {
          networkID: pool.network,
          subnetID: pool.subnet,
        },
      },
      ...(sourceType === 'volume'
        ? [
            {
              name: 'rootVolume',
              value: {
                sizeGiB: volumeSize,
                type: volumeType,
              },
            },
          ]
        : []),
    ],
  },
})

export const createKaapiClusterClassBody = ({
  name,
  namespace,
  isBYOCluster = false,
  gpuAddons = false,
  network,
  subnet,
  openstackCluster,
  nodePools,
  volumeType,
  volumeSize,
  sourceType,
  image,
  region,
  domain,
  tenant,
  kubernetesVersion,
  apiServerFlags,
  schedulerFlags,
  controllerManagerFlags,
  duInternalIP,
  isAirgapped,
  sshKey,
  replicas = 1,
}: kaapiClusterClassParams) => {
  const { infraDomain, infraName } = getInfraDetails()
  // Get cas-capi-version from the version
  const casCapiVersion = `v${kubernetesVersion
    .split('.')
    .slice(0, 2)
    .join('.')}`

  return {
    apiVersion: 'cluster.x-k8s.io/v1beta1',
    kind: 'Cluster',
    metadata: {
      name,
      namespace,
      labels: {
        'cas-capi-version': casCapiVersion,
        ...(gpuAddons ? { 'gpu-addons': 'enabled' } : {}),
        ...(isPrivateRegistryEnabled() ? { 'private-registry': 'enabled' } : {}),
      },
    },
    spec: {
      topology: {
        version: kubernetesVersion,
        workers: {
          machineDeployments: isBYOCluster
            ? [
                {
                  name: 'md-0',
                  class: 'byoh-md',
                  replicas: replicas,
                },
              ]
            : nodePools.map((pool, index) =>
                buildMachineDeployment({
                  pool,
                  index,
                  region,
                  openstackCluster,
                  image,
                  sourceType,
                  volumeSize,
                  volumeType,
                  sshKey,
                }),
              ),
        },
        variables: [
          {
            name: 'deploymentUnit',
            value: {
              name: infraName,
              domain: infraDomain,
              region: region,
              keystoneDomain: domain,
              keystoneTenant: tenant,
              ...(isAirgapped && duInternalIP && { type: 'onprem', vip: duInternalIP }),
            },
          },
          ...(!isBYOCluster
            ? [
                {
                  name: 'workerNode',
                  value: {
                    // This gets overridden by the machine deployment workerNode flavor though is mandatory to add in variables as well.
                    flavor: nodePools[0].flavor,
                    imageName: image,
                  },
                },
              ]
            : []),
          isBYOCluster
            ? {
                name: 'infrastructure',
                value: { type: 'byoh' },
              }
            : {
                name: 'network',
                value: {
                  networkID: network,
                  subnetID: subnet,
                },
              },
          // Optional: Customize kube-apiserver flags
          {
            name: 'apiServerFlags',
            value: apiServerFlags,
          },
          // Optional: Customize kube-controller-manager flags
          {
            name: 'controllerManagerFlags',
            value: controllerManagerFlags,
          },
          // Optional: Customize kube-scheduler flags
          {
            name: 'schedulerFlags',
            value: schedulerFlags,
          },
        ],
      },
    },
  }
}

interface NodeGroupTemplateParams {
  name: string
  openstackCluster: string
  nodePool: any
  volumeType?: string
  volumeSize?: number
  sourceType: string
  image: string
  sshKey?: SshKeyObject
}

export const createNodeGroupTemplateBody = ({
  name,
  openstackCluster,
  nodePool,
  volumeType,
  volumeSize,
  sourceType,
  image,
  sshKey,
}: NodeGroupTemplateParams) => [
  {
    op: 'add',
    path: '/spec/topology/workers/machineDeployments/-',
    value: {
      name: name,
      class: 'ost-md',
      metadata: {
        labels: {
          'pcd-kaapi.pf9.io/region': getActiveRegion(),
        },
        ...(nodePool.autoscaling
          ? {
              annotations: {
                'cluster.x-k8s.io/cluster-api-autoscaler-node-group-min-size': String(
                  nodePool.replicas,
                ),
                'cluster.x-k8s.io/cluster-api-autoscaler-node-group-max-size': String(
                  nodePool.autoscaling.max,
                ),
              },
            }
          : {}),
      },
      ...(!nodePool.autoscaling && { replicas: nodePool.replicas || 1 }),
      failureDomain: openstackCluster,
      variables: {
        overrides: [
          {
            name: 'workerNode',
            value: {
              flavor: nodePool.flavor,
              imageName: image,
              ...buildSshKeyVariables(sshKey),
            },
          },
          {
            name: 'network',
            value: {
              networkID: nodePool.network,
              subnetID: nodePool.subnet,
            },
          },
          ...(sourceType === 'volume'
            ? [
                {
                  name: 'rootVolume',
                  value: {
                    sizeGiB: volumeSize,
                    type: volumeType,
                  },
                },
              ]
            : []),
        ],
      },
    },
  },
]
